import React, { FC, useState, useEffect } from 'react'

import {
  getPublicPortal,
  getSsoSessionStatus,
  IPublicPortalData,
} from 'components/contentspace/api'
import {
  buildSsoAuthorizeUrl,
  getAutoLoginConfig,
  isAutoLoginBypassed,
} from 'components/contentspace/portal/autoLogin'
import {
  clearPortalSession,
  getPortalAuthCookie,
  setPortalAuthCookie,
  IPortalAuthData,
} from 'components/contentspace/sessionCookie'
import { GlobalPortalLoader } from 'routes/portal/components/GlobalPortalLoader'

import { usePortalParams } from '../hooks/usePortalParams'
import { AccessGate } from '../pages/PortalLms/components/AccessGate/AccessGate'
import { resolveAuthGateAction } from './auth-gate-action'

interface IContentSpaceAuthGateProps {
  children: React.ReactNode
}

export const ContentSpaceAuthGate: FC<IContentSpaceAuthGateProps> = ({ children }) => {
  const { companyId, portalId } = usePortalParams()

  const [portal, setPortal] = useState<IPublicPortalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [portalAuth, setPortalAuth] = useState<IPortalAuthData | null>(null)

  useEffect(() => {
    if (!companyId || !portalId) {
      return
    }

    let cancelled = false

    const searchStr = window.location.search || window.location.hash.split('?')[1] || ''
    const params = new URLSearchParams(searchStr)
    const isSsoCallback = params.has('sso')

    if (isSsoCallback) {
      params.delete('sso')
      const hashBase = window.location.hash.split('?')[0] || ''
      const remaining = params.toString()
      const cleanUrl = remaining
        ? `${window.location.pathname}${hashBase}?${remaining}`
        : `${window.location.pathname}${hashBase}`
      window.history.replaceState({}, '', cleanUrl)
    }

    const existingAuth = getPortalAuthCookie(portalId)
    if (existingAuth) {
      setPortalAuth(existingAuth)
    }

    const loadPortal = async () => {
      setLoading(true)
      try {
        const data = await getPublicPortal(companyId, portalId)
        if (cancelled) {
          return
        }
        setPortal(data)

        if (existingAuth) {
          setPortalAuthCookie(portalId, existingAuth)
        }

        const autoLoginConfig = getAutoLoginConfig(data.ssoConfigs)
        if (autoLoginConfig && !existingAuth && !isSsoCallback && !isAutoLoginBypassed(portalId)) {
          window.location.replace(
            buildSsoAuthorizeUrl(companyId, portalId, autoLoginConfig.id, window.location.href),
          )
          return
        }

        if (isSsoCallback || existingAuth?.ssoAuthenticated) {
          const ssoStatus = await getSsoSessionStatus(companyId, portalId)
          if (cancelled) {
            return
          }
          const action = resolveAuthGateAction({
            existingAuth,
            ssoStatus,
            autoLoginConfig: autoLoginConfig ?? null,
            isAutoLoginBypassed: isAutoLoginBypassed(portalId),
          })
          if (action.kind === 'keep' && action.refreshedAuth?.email) {
            // SSO = финальный identity provider. Пропускаем в children без
            // дополнительных форм (Your-Details / Email-Verification).
            const ssoAuthData: IPortalAuthData = {
              email: action.refreshedAuth.email,
              name: action.refreshedAuth.userName || undefined,
              verified: true,
              ssoAuthenticated: true,
              endUserId: action.refreshedAuth.endUserId,
            }
            setPortalAuthCookie(portalId, ssoAuthData)
            setPortalAuth(ssoAuthData)
          } else if (action.kind === 'clear-and-redirect-sso') {
            clearPortalSession(portalId)
            window.location.replace(
              buildSsoAuthorizeUrl(
                companyId,
                portalId,
                action.autoLoginConfigId,
                window.location.href,
              ),
            )
            return
          } else if (action.kind === 'clear-and-show-gate') {
            clearPortalSession(portalId)
            setPortalAuth(null)
          }
        }
      } catch (err: unknown) {
        if (cancelled) {
          return
        }
        setError((err as Error)?.message || 'Failed to load portal')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPortal()

    return () => {
      cancelled = true
    }
  }, [companyId, portalId])

  const hasMultipleAccessModes = (portal?.permissions?.accessModes?.length || 0) > 1
  const needsAuth =
    (portal?.permissions?.accessModes?.length || 0) > 0 &&
    !portal?.permissions?.accessModes?.includes('ANONYMOUS')

  const showAccessGate = (needsAuth || hasMultipleAccessModes) && !portalAuth

  if (loading) {
    return <GlobalPortalLoader reason='auth-gate' visible />
  }

  if (error || !portal) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error</h2>
        <p>{error || 'Portal not found'}</p>
      </div>
    )
  }

  if (showAccessGate) {
    return (
      <AccessGate
        companyId={companyId!}
        onAccessGranted={(userInfo) => {
          const authData: IPortalAuthData = {
            email: userInfo?.email,
            name: userInfo?.name,
            verified: userInfo?.verified || false,
          }
          setPortalAuthCookie(portalId!, authData)
          setPortalAuth(authData)
        }}
        permissions={portal.permissions}
        portalDescription={portal.description}
        portalId={portalId!}
        portalName={portal.name}
        ssoConfigs={portal.ssoConfigs}
      />
    )
  }

  return children as React.ReactElement
}
